import { useId, type ComponentProps } from 'react';
import { Select } from '@react-apps/design-system';
import { Tooltip } from '@/shared/components/tooltip';

type SelectProps = ComponentProps<typeof Select>;

/**
 * O `Select` dos filtros de busca com a dica num balão, e não em texto
 * embaixo do campo ("Escolha a Grande Área primeiro", "Carregando…"): o
 * balão aparece com o mouse em cima ou com o foco, pelo `Tooltip` da
 * plataforma. O texto segue ligado ao select por `aria-describedby`, num
 * `sr-only`, para o leitor de tela continuar dizendo por que ele está
 * bloqueado. O erro, que pede ação, segue visível embaixo, como no `Select`.
 *
 * O select bloqueado fica sem `pointer-events`: elemento de formulário
 * desabilitado não dispara evento de mouse, e o balão nunca abriria em cima
 * dele. Pelo mesmo motivo ele não recebe foco, então o envoltório entra na
 * ordem do Tab enquanto o campo está bloqueado, e o cursor de bloqueado fica
 * nele.
 */
export function HintSelect({
  hint,
  className = '',
  ...props
}: Omit<SelectProps, 'helperText'> & { hint?: string }) {
  const hintId = useId();
  if (!hint) return <Select className={className} {...props} />;
  // Com erro, o `Select` aponta o campo para a mensagem dele: a chave não vai,
  // porque um `aria-describedby={undefined}` explícito no spread o apagaria.
  const describedBy = props.error ? {} : { 'aria-describedby': hintId };
  return (
    <Tooltip
      trigger="hover"
      message={hint}
      // Bloqueado, o select não recebe foco: o envoltório entra no Tab para a
      // dica abrir também pelo teclado.
      focusable={!!props.disabled}
      triggerLabel={props.label ? `${props.label}: ${hint}` : hint}
      triggerClassName={`focus-visible:ring-(--color-accent) block rounded-md focus-visible:outline-none focus-visible:ring-2 ${props.disabled ? 'cursor-not-allowed' : ''}`}
    >
      <Select
        className={`disabled:pointer-events-none ${className}`}
        {...describedBy}
        {...props}
      />
      <span id={hintId} className="sr-only">
        {hint}
      </span>
    </Tooltip>
  );
}
